import CrossIcon from "@/assets/FF5A4F_cross_icon.svg?react";
import PlusIcon from "@/assets/FFFFFF_plus_icon.svg?react";
import EditIcon from "@/assets/colorable/pen_edit.svg?react";
import UploadIcon from "@/assets/colorable/upload.svg?react";
import Button from "@/components/Common/Button";
import clsxm from "@/lib/clsxm";
import {memo, useMemo, type FC} from "react";
import {useDropzone, type Accept} from "react-dropzone";
import {useTranslation} from "react-i18next";

const FileUpload: FC<{
  file?: File | string | null;
  onUpload(acceptedFiles: File[]): void;
  multiple?: boolean;
  accept?: Accept;
  variant?: "CIRCULAR" | "RECTANGULAR";
  disabled?: boolean;
}> = ({
  file,
  onUpload,
  multiple = false,
  accept = {
    "image/*": [],
  },
  variant = "CIRCULAR",
  disabled = false,
}) => {
  const {t} = useTranslation();
  const fileToShow = useMemo(() => (file instanceof File ? URL.createObjectURL(file) : file), [file]);

  const {getInputProps, getRootProps, isDragActive, isDragReject} = useDropzone({
    onDrop: onUpload,
    multiple,
    accept,
  });

  if (variant === "RECTANGULAR") {
    return (
      <div {...getRootProps()} className={`${disabled ? "pointer-events-none cursor-not-allowed" : "cursor-pointer"}`}>
        <input {...getInputProps()} />
        {
          <div
            className={clsxm(
              "border-neutral-grey-light hover:bg-neutral-white-dark/30 flex flex-col items-center justify-between gap-4 rounded-md border border-dashed p-5 transition-all duration-150 sm:flex-row",
              isDragReject && "border-danger-medium-red",
              isDragActive && !isDragReject && "border-primary-dark-solid",
            )}>
            <span className="text-neutral-grey-light flex-1 text-center text-sm">{t("or_drop_file_here")}</span>
            <Button
              onClick={() => void 0}
              shouldStopPropagation={false}
              buttonStyle="Blue"
              className="h-auto gap-2.5 px-8 py-4">
              <UploadIcon className="h-3 shrink-0" />
              {t("select_file")}
            </Button>
          </div>
        }
      </div>
    );
  }

  return (
    <div {...getRootProps()} className={`${disabled ? "pointer-events-none cursor-not-allowed" : "cursor-pointer"}`}>
      <input {...getInputProps()} />
      {!fileToShow ? (
        <div
          className={clsxm(
            "group border-primary-dark-solid hover:border-primary-midnight h-44 w-44 rounded-full border-2 border-dashed bg-transparent object-cover transition-all duration-150",
            isDragReject && "border-danger-medium-red",
            isDragActive && !isDragReject && "border-primary-midnight border-2",
          )}>
          <div className="flex h-full w-full items-center justify-center">
            {isDragReject ? (
              <CrossIcon
                className={clsxm(
                  "border-primary-light-solid bg-primary-dark-solid group-hover:bg-primary-midnight flex h-12 w-12 items-center justify-center rounded-full border p-3.5 shadow-md transition-all duration-150",
                  isDragActive && !isDragReject && "bg-primary-midnight",
                  isDragReject && "border-danger-medium-red bg-danger-light-red",
                )}
              />
            ) : (
              <PlusIcon
                className={clsxm(
                  "border-primary-light-solid bg-primary-dark-solid group-hover:bg-primary-midnight flex h-12 w-12 items-center justify-center rounded-full border p-3.5 shadow-md transition-all duration-150",
                  isDragActive && !isDragReject && "bg-primary-midnight",
                  isDragReject && "border-danger-medium-red bg-danger-light-red",
                )}
              />
            )}
          </div>
        </div>
      ) : (
        <div
          className={clsxm(
            "group border-primary-light-solid hover:border-primary-midnight relative h-44 w-44 rounded-full border-2 shadow-md transition-all duration-150",
            isDragReject && "border-danger-medium-red",
            isDragActive && !isDragReject && "border-primary-midnight border-2",
          )}>
          <img src={fileToShow} alt="" className="h-full w-full rounded-full object-cover" />
          <div
            className={clsxm(
              "border-primary-light-solid group-hover:border-primary-midnight absolute top-0 right-0 flex h-12 w-12 items-center justify-center rounded-full border bg-white p-3 shadow-lg transition-all duration-150",
              isDragActive && !isDragReject && "border-primary-midnight bg-primary-midnight",
              isDragReject && "border-danger-medium-red bg-danger-light-red",
            )}>
            {isDragReject ? (
              <CrossIcon className="size-4 shrink-0" />
            ) : (
              <EditIcon
                className={
                  "h-[18px] w-[18px] transition-all duration-150 " +
                  (isDragActive && !isDragReject ? "text-white" : "text-primary-dark-solid")
                }
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(FileUpload);
